import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import LazyImage from './LazyImage';

const RoomCard = ({ room }) => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Použiť images array ak existuje, inak len hlavný obrázok
  const images = room.images || [room.image];
  const hasMultipleImages = images.length > 1;

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <>
      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700 hover:border-copper-500 transition-all hover:shadow-xl hover:shadow-copper-500/10">
        <div className="relative h-80 md:h-96 overflow-hidden group">
          <LazyImage 
            src={room.image} 
            alt={room.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{room.name}</h3>
          
          {/* Beds and Bathroom Info - only for rooms with beds */}
          {room.beds && room.beds.length > 0 && (
            <div className="space-y-3 mb-4">
              {/* Beds */}
              <div className="flex items-center text-gray-700 dark:text-gray-300">
                <svg className="w-5 h-5 text-copper-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M2 6c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6zm2 0v12h16V6H4zm2 2h12v2H6V8zm0 4h12v2H6v-2z"/>
                  <circle cx="5" cy="9" r=".5" fill="currentColor"/>
                  <circle cx="19" cy="9" r=".5" fill="currentColor"/>
                </svg>
                <span className="text-sm">
                  {(() => {
                    // Počítame manželské postele a prístelky
                    let mainBedsCount = 0;
                    let extraBedsCount = 0;
                    
                    room.beds.forEach(bed => {
                      const bedType = bed.type.toLowerCase();
                      if (bedType.includes('manželská') || bedType.includes('double') || bedType.includes('małżeńskie') || 
                          bedType.includes('poschodová') || bedType.includes('bunk') || bedType.includes('piętrowe')) {
                        mainBedsCount += bed.count;
                      } else if (bedType.includes('prístelka') || bedType.includes('extra') || bedType.includes('dodatkowe') ||
                                 bedType.includes('gauč') || bedType.includes('sofa') || bedType.includes('kanapa')) {
                        extraBedsCount += bed.count;
                      } else {
                        // Ak nie je špecifikované, považujeme za hlavné lôžko
                        mainBedsCount += bed.count;
                      }
                    });
                    
                    const bedsLabel = mainBedsCount === 1 
                      ? t('accommodation.bedSingular').toLowerCase() 
                      : t('accommodation.bedPlural').toLowerCase();

                    if (mainBedsCount === 0 && extraBedsCount > 0) {
                      return `0 ${bedsLabel} + ${extraBedsCount} ${t('accommodation.extraBed').toLowerCase()}`;
                    } else if (extraBedsCount > 0) {
                      return `${mainBedsCount} ${bedsLabel} + ${extraBedsCount} ${t('accommodation.extraBed').toLowerCase()}`;
                    }
                    return `${mainBedsCount} ${bedsLabel}`;
                  })()}
                </span>
              </div>
              
              {/* Bathroom */}
              {room.bathroom && (
                <div className="flex items-center text-gray-700 dark:text-gray-300">
                  <svg className="w-5 h-5 text-copper-400 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="text-sm">
                    {room.bathroom.count} {t('accommodation.bathroom').toLowerCase()}, {room.bathroom.toilet} {t('accommodation.toilet').toLowerCase()}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Persons Count and Area */}
          <div className="space-y-2 mb-6">
            {room.maxPersons && (
              <div className="flex items-center text-gray-700 dark:text-gray-300">
                <span className="text-sm">{t('accommodation.personsCount')} {room.maxPersons}</span>
              </div>
            )}
            {room.area && (
              <div className="flex items-center text-gray-700 dark:text-gray-300">
                <span className="text-sm">{t('accommodation.area')} {room.area}</span>
              </div>
            )}
          </div>
          
          <div className="flex justify-center">
            <button
              onClick={() => {
                setIsModalOpen(true);
                setCurrentImageIndex(0);
              }}
              className="px-6 py-2 bg-copper-500 hover:bg-copper-600 text-white font-semibold rounded-lg transition-colors"
            >
              {t('accommodation.more')}
            </button>
          </div>
        </div>
      </div>

      {/* Modal with Gallery and Info */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm overflow-y-auto"
          onClick={() => {
            setIsModalOpen(false);
            setCurrentImageIndex(0);
          }}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg max-w-5xl w-full mx-2 md:mx-4 my-2 md:my-8 max-h-[95vh] overflow-y-auto border border-gray-300 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image Gallery Section */}
            <div className="relative flex items-center justify-center bg-black">
              {/* Close Button */}
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setCurrentImageIndex(0);
                }}
                className="absolute top-2 right-2 md:top-4 md:right-4 z-20 text-white hover:text-copper-400 transition-colors bg-gray-900/50 rounded-full p-1.5 md:p-2 hover:bg-gray-900/70"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Navigation Arrows - len ak sú viaceré obrázky */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-20 text-white hover:text-copper-400 transition-colors bg-gray-900/50 rounded-full p-2 md:p-3 hover:bg-gray-900/70"
                  >
                    <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-20 text-white hover:text-copper-400 transition-colors bg-gray-900/50 rounded-full p-2 md:p-3 hover:bg-gray-900/70"
                  >
                    <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Main Image */}
              <LazyImage 
                src={images[currentImageIndex]} 
                alt={room.name}
                className="max-h-[80vh] w-auto max-w-full object-contain"
              />

              {/* Image Counter - len ak sú viaceré obrázky */}
              {hasMultipleImages && (
                <div className="absolute bottom-2 md:bottom-4 left-1/2 transform -translate-x-1/2 z-20 bg-gray-900/50 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm">
                  {currentImageIndex + 1} / {images.length}
                </div>
              )}
            </div>

            {/* Room Info Section */}
            <div className="p-4 md:p-6 lg:p-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4 md:mb-6">{room.name}</h2>
              <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                {room.area && (
                  <div className="bg-gray-100 dark:bg-gray-700/50 p-3 md:p-4 rounded-lg">
                    <div className="text-copper-400 text-xs md:text-sm font-semibold mb-1">{t('accommodation.area')}</div>
                    <div className="text-gray-900 dark:text-white text-base md:text-lg">{room.area}</div>
                  </div>
                )}
                {room.maxPersons && (
                  <div className="bg-gray-100 dark:bg-gray-700/50 p-3 md:p-4 rounded-lg">
                    <div className="text-copper-400 text-xs md:text-sm font-semibold mb-1">{t('accommodation.personsCount')}</div>
                    <div className="text-gray-900 dark:text-white text-base md:text-lg">{room.maxPersons}</div>
                  </div>
                )}
              </div>

              {/* Beds Section */}
              {room.beds && room.beds.length > 0 && (
                <div className="mb-4 md:mb-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3">{t('accommodation.beds')}</h3>
                  <div className="bg-gray-100 dark:bg-gray-700/50 p-3 md:p-4 rounded-lg">
                    <ul className="space-y-2">
                      {room.beds.map((bed, index) => (
                        <li key={index} className="text-sm md:text-base text-gray-700 dark:text-gray-300">
                          <span className="font-semibold">{bed.count}×</span> {bed.type}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Bathroom Section */}
              {room.bathroom && (
                <div className="mb-4 md:mb-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3">
                    {t('accommodation.bathroom')}
                  </h3>
                  <div className="bg-gray-100 dark:bg-gray-700/50 p-3 md:p-4 rounded-lg">
                    <div className="text-sm md:text-base text-gray-700 dark:text-gray-300 mb-2 md:mb-3">
                      <span className="font-semibold">{room.bathroom.count}×</span> {t('accommodation.bathroom')} s {t('accommodation.toilet')}
                    </div>
                    {room.bathroom.equipment && room.bathroom.equipment.length > 0 && (
                      <div className="mt-2 md:mt-3">
                        <div className="text-copper-400 text-xs md:text-sm font-semibold mb-2">{t('accommodation.equipment')}</div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs md:text-sm text-gray-700 dark:text-gray-300">
                          {room.bathroom.equipment.map((item, index) => (
                            <li key={index} className="flex items-center">
                              <svg className="w-3 h-3 md:w-4 md:h-4 text-copper-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Location Section */}
              {room.location && (
                <div className="mb-4 md:mb-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3">{t('accommodation.additionalInfo')}</h3>
                  <div className="bg-gray-100 dark:bg-gray-700/50 p-3 md:p-4 rounded-lg space-y-2">
                    {room.location.floor && (
                      <div className="text-sm md:text-base text-gray-700 dark:text-gray-300">
                        <span className="font-semibold">{t('accommodation.location')}:</span> {room.location.floor}. {t('accommodation.floor')}
                      </div>
                    )}
                    {room.location.view && room.location.view.length > 0 && (
                      <div className="text-sm md:text-base text-gray-700 dark:text-gray-300">
                        <span className="font-semibold">{t('accommodation.view')}:</span> {room.location.view.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {room.description && (
                <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed">{room.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RoomCard;

